import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Typography } from "../../interfaces/typography";
import { useSessionDetails } from "../../lib/redux/sessions/data-hook";
import { TranscriptHeader } from "./TranscriptView/TranscriptHeader";
import { TranscriptBody } from "./TranscriptView/TranscriptBody";
import { AIGenerationSection } from "./AIGenerationSection";
import { CommentPanel } from "./TranscriptView/CommentPanel";
import { TranscriptAudio } from "./TranscriptView";
import "./MainView.css";
import { calculateTotalDuration } from "./TranscriptView/TranscriptAudio/timeUtils";
import { SEC_TO_MS } from "../../cfg/const";
import { SessionDetailView, viewCanSeeComments, viewCanSeeHeader } from "./types";

interface SessionDetailsProps {
  sessionId: string;
  view: SessionDetailView;
  inView?: boolean;
  onClose?: () => void;
}

const LoadingState = () => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-6">
      {/* Animated loading spinner */}
      <div className="relative w-16 h-16">
        <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
        <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
      </div>
      
      {/* Loading text with animated dots */}
      <div className="flex items-center gap-1">
        <Typography variant="h3" color="gray">
          Loading transcript
        </Typography>
        <span className="animate-[dots_1.5s_ease-in-out_infinite] text-indigo-600">.</span>
        <span className="animate-[dots_1.5s_ease-in-out_infinite_0.2s] text-indigo-600">.</span>
        <span className="animate-[dots_1.5s_ease-in-out_infinite_0.4s] text-indigo-600">.</span>
      </div>
      
      <Typography variant="mediumParagraph" color="gray" className="text-center max-w-md">
        Please wait while we prepare your transcript data
      </Typography>
    </div>
  </div>
);

const NoDataState = ({ onRetry }: { onRetry: () => void }) => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-6 max-w-lg text-center p-8">
      {/* No data icon */}
      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
        <svg 
          className="w-12 h-12 text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
          />
        </svg>
      </div>
      
      <div className="flex flex-col gap-3">
        <Typography variant="h2" color="gray">
          No Transcript Available
        </Typography>
        <Typography variant="mediumParagraph" color="gray">
          We couldn't find any transcript data for this session. This might happen if there was an issue processing the transcript.
        </Typography>
      </div>

      <button 
        className="px-4 py-2 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 transition-colors"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  </div>
);

export function SessionDetails({
  sessionId,
  view,
  inView=true,
  onClose
}: SessionDetailsProps) {
  const { sessionData, isLoading, refresh } = useSessionDetails({ sessionId });
  
  // Audio playback state
  const [selectedTranscriptIdx, setSelectedTranscriptIdx] = useState<number | undefined>();
  const [currentTimeInMs, setCurrentTimeInMs] = useState(0);
  const currentTimeInMsRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState(false);
  const [speed, setSpeed] = useState(1); // Default to 1x (index 2)
  const [previousPlayState, setPreviousPlayState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const totalDuration = useMemo(() => {
    if (!sessionData) return 0
    const scheduledStart = new Date(sessionData.session.scheduled_start)
    if (!scheduledStart) return 0
    return calculateTotalDuration(sessionData.audio ?? [], scheduledStart)
  }, [sessionData])

  useEffect(() => {
    if (selectedTranscriptIdx !== undefined && sessionData && sessionData.turns.length > selectedTranscriptIdx) {
      const transcript = sessionData?.turns[selectedTranscriptIdx]
      if (transcript) { setCurrentTimeInMs(transcript.start * SEC_TO_MS) }
    }
  }, [selectedTranscriptIdx])
  
  // Reset playback state when session data changes
  useEffect(() => {
    currentTimeInMsRef.current = 0;
    setCurrentTimeInMs(0);
    setIsPlaying(false);
    setSelectedTranscriptIdx(undefined);
  }, [sessionId, inView]);

  const handleUpdateTime = (time?: number, isActive?: boolean, prevActive?: boolean) => {
    if (time !== undefined) {
      currentTimeInMsRef.current = time;
      setCurrentTimeInMs(time);
    }
    setIsPlaying(isActive ?? false);
    setPreviousPlayState(prevActive ?? false);
  };

  const toggleCommentPanel = () => setIsCommentPanelOpen(!isCommentPanelOpen)

  // Timer effect for updating current time
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start a new timer if playing
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        const newTime = currentTimeInMsRef.current + (speed * 250);
        if (newTime > totalDuration) {
          setIsPlaying(false);
        }
        const clampedTime = Math.min(newTime, totalDuration)
        currentTimeInMsRef.current = clampedTime
        setCurrentTimeInMs(clampedTime);
      }, 250);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, speed]); // Re-create timer when isPlaying or speed changes

  if (isLoading) return <LoadingState />;
  if (!sessionData) return <NoDataState onRetry={refresh} />;

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {viewCanSeeHeader(view) && <TranscriptHeader 
        data={sessionData} 
        onClose={onClose}
        view={view}
      />}
      
      {/* AI Generation Section */}
      <AIGenerationSection sessionData={sessionData} view={view} />
      
      <div className="flex-grow overflow-auto flex">
        <TranscriptBody 
          view={view}
          data={sessionData}
          currentTimeInMs={currentTimeInMs}
          isPlaying={isPlaying}
          updateSelectedTranscript={(idx) => {
            setSelectedTranscriptIdx(idx)
              if (isPlaying) {
                setIsPlaying(false);
              }
          }}
          selectedTranscriptIdx={selectedTranscriptIdx}
        />
      </div>

      {/* General Comments Panel */}
      {viewCanSeeComments(view) && sessionData && (
        <CommentPanel
          sessionData={sessionData}
          isOpen={isCommentPanelOpen}
          onToggle={toggleCommentPanel}
          onScrollToTranscript={setSelectedTranscriptIdx}
        />
      )}

      <TranscriptAudio
        data={sessionData}
        currentTimeInMs={currentTimeInMs}
        isPlaying={isPlaying}
        updateTime={handleUpdateTime}
        setSpeed={setSpeed}
        speed={speed}
        previousPlayState={previousPlayState}
        totalDuration={totalDuration}
      />
    </div>
  );
}
